// Template element types
export type TemplateElementType = 'image' | 'text' | 'shape' | 'icon';

// Curated icon library — lucide-static icon names. Small on purpose (25
// icons for ad/marketing work). Expand this list as needed. Adding a name
// here automatically makes the icon available in the editor; the renderer
// pulls the SVG from lucide-static at runtime.
// Lucide excludes brand/social icons for trademark reasons.
// Using generic contact icons as placeholders for social — add brand icons
// via a separate package (e.g., simple-icons) in a follow-up if needed.
export type IconName =
  // Contact
  | 'phone' | 'mail' | 'globe' | 'map-pin' | 'message-circle'
  // Contact extras (generic link/share)
  | 'at-sign' | 'link' | 'share-2' | 'send' | 'external-link'
  // Commerce
  | 'shopping-cart' | 'credit-card' | 'tag' | 'star' | 'heart'
  // Action
  | 'check' | 'arrow-right' | 'chevron-right' | 'clock' | 'calendar'
  // People / brand accents
  | 'user' | 'users' | 'building' | 'zap' | 'shield' | 'award';

export const ICON_LIBRARY: { name: IconName; label: string; group: string }[] = [
  { name: 'phone', label: 'Phone', group: 'Contact' },
  { name: 'mail', label: 'Mail', group: 'Contact' },
  { name: 'globe', label: 'Globe', group: 'Contact' },
  { name: 'map-pin', label: 'Location', group: 'Contact' },
  { name: 'message-circle', label: 'Message', group: 'Contact' },
  { name: 'at-sign', label: 'At sign', group: 'Share' },
  { name: 'link', label: 'Link', group: 'Share' },
  { name: 'share-2', label: 'Share', group: 'Share' },
  { name: 'send', label: 'Send', group: 'Share' },
  { name: 'external-link', label: 'External link', group: 'Share' },
  { name: 'shopping-cart', label: 'Cart', group: 'Commerce' },
  { name: 'credit-card', label: 'Credit card', group: 'Commerce' },
  { name: 'tag', label: 'Tag', group: 'Commerce' },
  { name: 'star', label: 'Star', group: 'Commerce' },
  { name: 'heart', label: 'Heart', group: 'Commerce' },
  { name: 'check', label: 'Check', group: 'Action' },
  { name: 'arrow-right', label: 'Arrow right', group: 'Action' },
  { name: 'chevron-right', label: 'Chevron right', group: 'Action' },
  { name: 'clock', label: 'Clock', group: 'Action' },
  { name: 'calendar', label: 'Calendar', group: 'Action' },
  { name: 'user', label: 'User', group: 'People' },
  { name: 'users', label: 'Users', group: 'People' },
  { name: 'building', label: 'Building', group: 'People' },
  { name: 'zap', label: 'Zap', group: 'Brand' },
  { name: 'shield', label: 'Shield', group: 'Brand' },
  { name: 'award', label: 'Award', group: 'Brand' },
];
export type TextAlign = 'left' | 'center' | 'right';
export type FontWeight = 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
export type TextWrapStyle = 'normal' | 'break-word' | 'break-all' | 'nowrap';

// Template layout types for auto-selection
export type TemplateLayoutType = 'overlay' | 'split' | 'typography' | 'generic';
// Individual image source types a template can work with
export type ImageSourceType = 'stock' | 'pastel' | 'ai' | 'asset';
// suitableFor is stored as comma-separated ImageSourceTypes or 'all'
export type TemplateSuitableFor = string;
export type ContentCategoryAffinity = 'education' | 'connection' | 'proof' | 'promotion' | 'any';

// Template color mode for dynamic color theming
export type TemplateColorMode = 'fixed' | 'brand' | 'adaptive';

// Shape types
export type ShapeType = 'rectangle' | 'circle' | 'ellipse' | 'parallelogram' | 'polygon';

// Image clip mask types
export type ImageClipShape = 'rectangle' | 'parallelogram' | 'polygon';

// Polygon vertex — normalized 0..100 of the element's bounding box
export interface PolygonPoint {
  x: number;
  y: number;
}

// Named polygon presets for quick shape picking in the editor.
// Each preset is a PolygonPoint[] in (0..100, 0..100) coords.
// Listing order matters — the editor shows them in this order.
export type PolygonPresetId =
  | 'triangle'
  | 'trapezoid-right'
  | 'trapezoid-left'
  | 'chevron-right'
  | 'chevron-left'
  | 'pentagon'
  | 'hexagon'
  | 'arrow-right';

export const POLYGON_PRESETS: Record<PolygonPresetId, { label: string; points: PolygonPoint[] }> = {
  'triangle': {
    label: 'Triangle',
    points: [
      { x: 50, y: 0 },
      { x: 100, y: 100 },
      { x: 0, y: 100 },
    ],
  },
  'trapezoid-right': {
    // Top flat, left side vertical, bottom slopes down-right, right side extends full height.
    // Matches the StormGuard-style banner: right corner goes further down than left.
    label: 'Trapezoid (right longer)',
    points: [
      { x: 0, y: 0 },
      { x: 100, y: 0 },
      { x: 100, y: 100 },
      { x: 0, y: 50 },
    ],
  },
  'trapezoid-left': {
    // Mirror of trapezoid-right: left side extends full height.
    label: 'Trapezoid (left longer)',
    points: [
      { x: 0, y: 0 },
      { x: 100, y: 0 },
      { x: 100, y: 50 },
      { x: 0, y: 100 },
    ],
  },
  'chevron-right': {
    label: 'Chevron ▶',
    points: [
      { x: 0, y: 0 },
      { x: 75, y: 0 },
      { x: 100, y: 50 },
      { x: 75, y: 100 },
      { x: 0, y: 100 },
    ],
  },
  'chevron-left': {
    label: 'Chevron ◀',
    points: [
      { x: 25, y: 0 },
      { x: 100, y: 0 },
      { x: 100, y: 100 },
      { x: 25, y: 100 },
      { x: 0, y: 50 },
    ],
  },
  'pentagon': {
    label: 'Pentagon',
    points: [
      { x: 50, y: 0 },
      { x: 100, y: 38 },
      { x: 81, y: 100 },
      { x: 19, y: 100 },
      { x: 0, y: 38 },
    ],
  },
  'hexagon': {
    label: 'Hexagon',
    points: [
      { x: 25, y: 0 },
      { x: 75, y: 0 },
      { x: 100, y: 50 },
      { x: 75, y: 100 },
      { x: 25, y: 100 },
      { x: 0, y: 50 },
    ],
  },
  'arrow-right': {
    label: 'Arrow ▶',
    points: [
      { x: 0, y: 30 },
      { x: 60, y: 30 },
      { x: 60, y: 0 },
      { x: 100, y: 50 },
      { x: 60, y: 100 },
      { x: 60, y: 70 },
      { x: 0, y: 70 },
    ],
  },
};

// Gradient types
export type GradientType = 'none' | 'linear' | 'radial';

export interface GradientStop {
  color: string;
  stop: number; // 0-1
}

// Dynamic text types for AI-generated content
export type DynamicTextType = 'headline' | 'tagline' | 'cta' | 'quote' | 'stat' | 'custom';

// Asset reference types for image elements
export type AssetReferenceType = 'fixed' | 'workspace_logo' | 'post_image';

// Character limits for each dynamic text type
export const DYNAMIC_TEXT_LIMITS: Record<DynamicTextType, { min: number; max: number; label: string }> = {
  headline: { min: 15, max: 50, label: 'Headline (3-8 words)' },
  tagline: { min: 30, max: 80, label: 'Tagline (5-12 words)' },
  cta: { min: 10, max: 30, label: 'Call-to-Action (2-5 words)' },
  quote: { min: 50, max: 150, label: 'Quote (10-20 words)' },
  stat: { min: 1, max: 8, label: 'Stat/Number (e.g., 0%, 3x, 24/7)' },
  custom: { min: 10, max: 200, label: 'Custom Prompt' },
};

// Transform properties for positioning elements
export interface ElementTransform {
  positionX: number;  // Percentage (0-100) from left
  positionY: number;  // Percentage (0-100) from top
  width: number;      // Percentage of canvas width
  height: number;     // Percentage of canvas height
  rotation: number;   // Degrees
  /** Mirror element on its X axis (left ↔ right). */
  flipHorizontal?: boolean;
  /** Mirror element on its Y axis (top ↔ bottom). */
  flipVertical?: boolean;
}

// Base element properties
export interface BaseElement {
  id: string;
  templateId: string;
  elementType: TemplateElementType;
  transform: ElementTransform;
  zIndex: number;
  opacity: number;
  name?: string;
  createdAt: string;
  /**
   * When the parent template has `brandColor` set, only elements with
   * `usesBrandColor: true` get their colors swapped when the brand color
   * changes via the Theme picker. Templates without `brandColor` ignore
   * this flag and fall back to heuristic accent detection.
   */
  usesBrandColor?: boolean;
}

// Image element (brand asset overlay)
export interface ImageElement extends BaseElement {
  elementType: 'image';
  assetId?: string;   // Reference to brand asset
  imageUrl?: string;  // Direct URL (fallback or for preview)
  assetReferenceType?: AssetReferenceType;  // 'fixed' (default) or 'workspace_logo'
  // Clip mask — default 'rectangle' (no clip)
  clipShape?: ImageClipShape;
  clipSkewAngle?: number;  // degrees, -45..45, used when clipShape === 'parallelogram'
  clipPolygonPoints?: PolygonPoint[];  // 3..8 points, used when clipShape === 'polygon'
}

// Text element (brand text overlay)
export interface TextElement extends BaseElement {
  elementType: 'text';
  textContent: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: FontWeight;
  textColor: string;
  textAlign: TextAlign;
  // Text fitting options
  autoSize?: boolean;  // When true, text auto-fits within bounding box (wrapping + shrinking)
  wrapStyle?: TextWrapStyle;  // Text wrapping behavior
  // Text outline/stroke
  outlineColor?: string;  // Outline color (e.g., '#000000')
  outlineWidth?: number;  // Outline width in pixels
  // Text enhancements
  letterSpacing?: number;  // Letter spacing in pixels (default 0)
  lineHeight?: number;     // Line height multiplier (default 1.3)
  // Text shadow control (default true for backward compat)
  textShadowEnabled?: boolean;
  // Dynamic text properties (AI-generated)
  isDynamic?: boolean;
  dynamicType?: DynamicTextType;
  dynamicPrompt?: string;
}

// Shape element (colored rectangles, overlays, dividers)
export interface ShapeElement extends BaseElement {
  elementType: 'shape';
  backgroundColor: string;  // Fill color (e.g., 'rgba(0,0,0,0.6)' or '#1B2838')
  borderRadius: number;     // Corner radius in pixels (0 = square corners)
  // Shape type
  shapeType?: ShapeType;    // 'rectangle' (default), 'circle', 'ellipse', 'parallelogram', 'polygon'
  // Parallelogram skew (degrees, -45..45). Ignored unless shapeType === 'parallelogram'.
  skewAngle?: number;
  // Polygon vertices (3..8 points, 0..100 of bounding box). Used when shapeType === 'polygon'.
  polygonPoints?: PolygonPoint[];
  // Gradient fill (overrides backgroundColor when active)
  gradientType?: GradientType;
  gradientColors?: GradientStop[];
  gradientAngle?: number;   // Degrees for linear gradient direction
  // Shadow
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  // Border/stroke
  borderColor?: string;
  borderWidth?: number;
}

// Icon element — references a name from the curated icon library (lucide).
// Renderer resolves the name to an SVG at draw time, applies iconColor.
export interface IconElement extends BaseElement {
  elementType: 'icon';
  iconName: IconName;
  iconColor: string;  // CSS color (hex, rgb, rgba)
}

// Union type for any element
export type TemplateElement = ImageElement | TextElement | ShapeElement | IconElement;

// Template definition
export interface Template {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string;
  isDefault: boolean;
  isGlobal?: boolean;
  colorMode?: TemplateColorMode;
  isActive?: boolean;
  layoutType?: TemplateLayoutType;
  suitableFor?: TemplateSuitableFor;
  contentCategoryAffinity?: ContentCategoryAffinity;
  /**
   * Explicit brand/accent color (hex). When set, the Theme Color picker
   * swaps THIS color → user's choice across only `usesBrandColor` elements.
   * When NULL, the client falls back to heuristic accent detection.
   */
  brandColor?: string;
  elements: TemplateElement[];
  createdAt: string;
  updatedAt: string;
}

// Template without elements (for list views)
export interface TemplateSummary {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string;
  isDefault: boolean;
  isGlobal?: boolean;
  colorMode?: TemplateColorMode;
  isActive?: boolean;
  layoutType?: TemplateLayoutType;
  suitableFor?: TemplateSuitableFor;
  contentCategoryAffinity?: ContentCategoryAffinity;
  brandColor?: string;
  elementCount: number;
  createdAt: string;
  updatedAt: string;
}

// API request types
export interface CreateTemplateRequest {
  name: string;
  description?: string;
  canvasWidth?: number;
  canvasHeight?: number;
  backgroundColor?: string;
  isGlobal?: boolean;
  colorMode?: TemplateColorMode;
  isActive?: boolean;
  layoutType?: TemplateLayoutType;
  suitableFor?: TemplateSuitableFor;
  contentCategoryAffinity?: ContentCategoryAffinity;
  brandColor?: string;
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  canvasWidth?: number;
  canvasHeight?: number;
  backgroundColor?: string;
  isDefault?: boolean;
  colorMode?: TemplateColorMode;
  isActive?: boolean;
  layoutType?: TemplateLayoutType;
  suitableFor?: TemplateSuitableFor;
  contentCategoryAffinity?: ContentCategoryAffinity;
  brandColor?: string | null;
}

export interface CreateElementRequest {
  elementType: TemplateElementType;
  // Transform
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  rotation?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  zIndex?: number;
  opacity?: number;
  name?: string;
  // Image-specific
  assetId?: string;
  imageUrl?: string;
  assetReferenceType?: AssetReferenceType;
  clipShape?: ImageClipShape;
  clipSkewAngle?: number;
  clipPolygonPoints?: PolygonPoint[];
  // Text-specific
  textContent?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: FontWeight;
  textColor?: string;
  textAlign?: TextAlign;
  autoSize?: boolean;
  wrapStyle?: TextWrapStyle;
  outlineColor?: string;
  outlineWidth?: number;
  letterSpacing?: number;
  lineHeight?: number;
  // Text shadow control
  textShadowEnabled?: boolean;
  // Shape-specific
  backgroundColor?: string;
  borderRadius?: number;
  shapeType?: ShapeType;
  skewAngle?: number;
  polygonPoints?: PolygonPoint[];
  // Shape gradient
  gradientType?: GradientType;
  gradientColors?: GradientStop[];
  gradientAngle?: number;
  // Shape shadow
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  // Shape border
  borderColor?: string;
  borderWidth?: number;
  // Dynamic text
  isDynamic?: boolean;
  dynamicType?: DynamicTextType;
  dynamicPrompt?: string;
  // Icon-specific
  iconName?: IconName;
  iconColor?: string;
  // Brand color opt-in
  usesBrandColor?: boolean;
}

export interface UpdateElementRequest extends Partial<CreateElementRequest> {}

// Canvas preset sizes for different platforms
export const CANVAS_PRESETS = {
  instagram_square: { width: 1080, height: 1080, label: 'Instagram Square (1:1)' },
  instagram_portrait: { width: 1080, height: 1350, label: 'Instagram Portrait (4:5)' },
  instagram_story: { width: 1080, height: 1920, label: 'Instagram Story (9:16)' },
  facebook_post: { width: 1200, height: 630, label: 'Facebook Post' },
  twitter_post: { width: 1200, height: 675, label: 'Twitter Post' },
  linkedin_post: { width: 1200, height: 627, label: 'LinkedIn Post' },
} as const;

export type CanvasPreset = keyof typeof CANVAS_PRESETS;

// Preview format presets (for viewing posts in different aspect ratios)
// These are separate from CANVAS_PRESETS - they're for display/preview only
export const PREVIEW_FORMATS = {
  square: { aspectRatio: 1, label: 'Square (1:1)' },
  vertical: { aspectRatio: 9/16, label: 'Vertical (9:16)' },
  portrait: { aspectRatio: 4/5, label: 'Portrait (4:5)' },
  widescreen: { aspectRatio: 16/9, label: 'Widescreen (16:9)' },
} as const;

export type PreviewFormat = keyof typeof PREVIEW_FORMATS;

// Simplified canvas format presets for template editor
// These match the generation format selector for consistency
export const EDITOR_CANVAS_FORMATS = {
  square: { width: 1080, height: 1080, label: 'Square (1:1)' },
  wide: { width: 1200, height: 630, label: 'Wide (16:9)' },
  vertical: { width: 1080, height: 1920, label: 'Vertical (9:16)' },
} as const;

export type EditorCanvasFormat = keyof typeof EDITOR_CANVAS_FORMATS;

// Available fonts for the editor
// System fonts + Google Fonts (loaded via FontFace API before render)
export const AVAILABLE_FONTS = [
  // System fonts
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Impact',
  'Courier New',
  // Google Fonts (loaded dynamically)
  'Poppins',
  'Playfair Display',
  'DM Sans',
  'Inter',
  'Outfit',
  'Space Grotesk',
  'Bebas Neue',
  'Oswald',
  'Raleway',
  'Lora',
  'Sora',
  'Manrope',
  'Montserrat',
] as const;

// Google Fonts that need to be loaded via FontFace API
export const GOOGLE_FONTS = [
  'Poppins',
  'Playfair Display',
  'DM Sans',
  'Inter',
  'Outfit',
  'Space Grotesk',
  'Bebas Neue',
  'Oswald',
  'Raleway',
  'Lora',
  'Sora',
  'Manrope',
  'Montserrat',
] as const;

// Text wrap style options
export const TEXT_WRAP_STYLES: { value: TextWrapStyle; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'break-word', label: 'Break Word' },
  { value: 'break-all', label: 'Break All' },
  { value: 'nowrap', label: 'No Wrap' },
];

// Default values for new elements
export const DEFAULT_IMAGE_ELEMENT: Omit<ImageElement, 'id' | 'templateId' | 'createdAt'> = {
  elementType: 'image',
  transform: {
    positionX: 75,
    positionY: 75,
    width: 20,
    height: 20,
    rotation: 0,
  },
  zIndex: 0,
  opacity: 1,
  name: 'Image',
};

export const DEFAULT_TEXT_ELEMENT: Omit<TextElement, 'id' | 'templateId' | 'createdAt'> = {
  elementType: 'text',
  textContent: 'Your Text',
  fontFamily: 'Arial',
  fontSize: 32,
  fontWeight: 'bold',
  textColor: '#ffffff',
  textAlign: 'center',
  autoSize: true,  // Default to auto-size for better UX
  wrapStyle: 'normal',
  outlineColor: undefined,
  outlineWidth: 0,
  letterSpacing: 0,
  lineHeight: 1.3,
  transform: {
    positionX: 50,
    positionY: 90,
    width: 80,
    height: 10,
    rotation: 0,
  },
  zIndex: 0,
  opacity: 1,
  name: 'Text',
  isDynamic: false,
  dynamicType: undefined,
  dynamicPrompt: undefined,
};

export const DEFAULT_ICON_ELEMENT: Omit<IconElement, 'id' | 'templateId' | 'createdAt'> = {
  elementType: 'icon',
  iconName: 'star',
  iconColor: '#000000',
  transform: {
    positionX: 50,
    positionY: 50,
    width: 8,
    height: 8,
    rotation: 0,
  },
  zIndex: 0,
  opacity: 1,
  name: 'Icon',
};

export const DEFAULT_SHAPE_ELEMENT: Omit<ShapeElement, 'id' | 'templateId' | 'createdAt'> = {
  elementType: 'shape',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  borderRadius: 0,
  shapeType: 'rectangle',
  skewAngle: 0,
  gradientType: 'none',
  transform: {
    positionX: 50,
    positionY: 75,
    width: 90,
    height: 30,
    rotation: 0,
  },
  zIndex: 0,
  opacity: 1,
  name: 'Shape',
};
